// src/pages/ServisKaydiOlustur.js
// Yeni Servis Kaydı Oluşturma Sayfası - v4 Refactored

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Typography, FormControl, InputLabel, Select, MenuItem,
    Button, TextField, Divider, Stack, Chip, CircularProgress, Grid, Alert,
    FormControlLabel, Checkbox
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import MaterialServiceSelectorModal from '../components/modals/MaterialServiceSelectorModal';
import CustomerFormModal from '../components/modals/CustomerFormModal';
import CustomerDeviceSelector from '../components/features/CustomerDeviceSelector';
import { Loading } from '../components/common/Loading';
import GlassCard from '../ui/glass/components/GlassCard';

// Modül import'ları
import { useServiceForm, ServiceItemsTable } from '../modules/serviceRecords';
import { db } from '../firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { findDuplicateDeviceBySerial, duplicateDeviceMessage } from '../utils/deviceDedup';

const ServisKaydiOlustur = () => {
    const { id } = useParams();
    const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);

    // Custom hook ile tüm form yönetimi
    const {
        loading,
        isEditMode,
        isSaving,
        customers,
        devices,
        selectedCustomerId,
        selectedDeviceId,
        arizaTanimi,
        yapilanIslemler,
        kullanilanParcalar,
        toplamTutar,
        odenenTutar,
        odemeTipi,
        servisNotlari,
        teslimTarihi,
        setSelectedCustomerId,
        setSelectedDeviceId,
        setArizaTanimi,
        setYapilanIslemler,
        setOdenenTutar,
        setOdemeTipi,
        setServisNotlari,
        setTeslimTarihi,

        // Yeni Alanlar Setter
        setBakimTuru,
        setOksijenOrani,
        setBasincDegeri,
        setCalismaSuresi,
        // Yeni Alanlar State
        bakimTuru,
        oksijenOrani,
        basincDegeri,
        calismaSuresi,
        handleMaterialSelect,
        handleRemoveParca,
        handleMiktarChange,
        handleFiyatChange,
        handleSubmit,
        // FAZ 4 - Ek Teknik Alanlar
        cihazHurdada,
        setCihazHurdada,
        teslimAlmaTarihi,
        setTeslimAlmaTarihi,
        teslimSaati,
        setTeslimSaati
    } = useServiceForm(id);

    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);



    if (loading) {
        return <Loading message="Yükleniyor..." />;
    }

    return (
        <Box sx={{ p: { xs: 1, md: 2 } }}>
            {/* Header Section */}
            <GlassCard sx={{ mb: 3, p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {isEditMode ? 'Servis Kaydını Düzenle' : 'Yeni Servis Kaydı Oluştur'}
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {isEditMode ? 'Mevcut servis kaydı bilgilerini güncelleyebilirsiniz.' : 'Sisteme yeni bir servis arıza/bakım kaydı ekleyin.'}
                        </Typography>
                    </Box>
                </Box>
            </GlassCard>

            {/* Main Form Content */}
            <GlassCard sx={{ p: 4 }}>
                {/* Müşteri ve Cihaz Seçimi */}
                <CustomerDeviceSelector
                    customers={customers}
                    devices={devices}
                    selectedCustomerId={selectedCustomerId}
                    selectedDeviceId={selectedDeviceId}
                    teslimTarihi={teslimTarihi}
                    setSelectedCustomerId={setSelectedCustomerId}
                    setSelectedDeviceId={setSelectedDeviceId}
                    setTeslimTarihi={setTeslimTarihi}
                    onAddCustomer={() => setIsCustomerModalOpen(true)}
                />

                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={3}>
                        <DatePicker
                            label="Teslim Alma Tarihi"
                            value={teslimAlmaTarihi}
                            onChange={setTeslimAlmaTarihi}
                            slotProps={{ textField: { fullWidth: true } }}
                            format="dd/MM/yyyy"
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            label="Teslim Saati (Opsiyonel)"
                            fullWidth
                            value={teslimSaati}
                            onChange={(e) => setTeslimSaati(e.target.value)}
                            placeholder="Örn: 14:30"
                            helperText="SS:DD formatında"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={cihazHurdada}
                                    onChange={(e) => setCihazHurdada(e.target.checked)}
                                    color="error"
                                />
                            }
                            label="Cihaz hurdada mı?"
                            sx={{ mt: 1 }}
                        />
                    </Grid>
                </Grid>

                {/* Servis Detayları */}
                <Divider sx={{ my: 4 }}><Chip label="Servis Detayları" sx={{ fontWeight: 500 }} /></Divider>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Bakım Türü</InputLabel>
                            <Select
                                value={bakimTuru}
                                label="Bakım Türü"
                                onChange={(e) => setBakimTuru(e.target.value)}
                            >
                                <MenuItem value="">Seçiniz</MenuItem>
                                <MenuItem value="Periyodik Bakım">Periyodik Bakım</MenuItem>
                                <MenuItem value="Arıza Onarım">Arıza Onarım</MenuItem>
                                <MenuItem value="Kurulum/Montaj">Kurulum/Montaj</MenuItem>
                                <MenuItem value="Cihaz Değişimi">Cihaz Değişimi</MenuItem>
                                <MenuItem value="SGK">SGK</MenuItem>
                                <MenuItem value="Vefat">Vefat</MenuItem>
                                <MenuItem value="Diğer">Diğer</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            label="Oksijen Oranı (%)"
                            fullWidth
                            value={oksijenOrani}
                            onChange={e => setOksijenOrani(e.target.value)}
                            placeholder="Örn: 95"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Basınç Durumu</InputLabel>
                            <Select
                                value={basincDegeri}
                                label="Basınç Durumu"
                                onChange={e => setBasincDegeri(e.target.value)}
                            >
                                <MenuItem value="Başarılı">Başarılı</MenuItem>
                                <MenuItem value="Başarısız">Başarısız</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Çalışma Süresi (Saat)"
                            fullWidth
                            value={calismaSuresi}
                            onChange={e => setCalismaSuresi(e.target.value)}
                            placeholder="Örn: 1530"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Arıza Tanımı / Müşteri Talebi"
                            multiline
                            rows={4}
                            fullWidth
                            value={arizaTanimi}
                            onChange={e => setArizaTanimi(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Yapılan İşlemler / Onarım Notları"
                            multiline
                            rows={4}
                            fullWidth
                            value={yapilanIslemler}
                            onChange={e => setYapilanIslemler(e.target.value)}
                        />
                    </Grid>
                </Grid>

                {/* Malzemeler / Hizmetler */}
                <Divider sx={{ my: 4 }}><Chip label="Kullanılan Malzemeler / Hizmetler" sx={{ fontWeight: 500 }} /></Divider>

                <ServiceItemsTable
                    items={kullanilanParcalar}
                    onRemove={handleRemoveParca}
                    onMiktarChange={handleMiktarChange}
                    onFiyatChange={handleFiyatChange}
                />

                <Button
                    onClick={() => setIsMaterialModalOpen(true)}
                    variant="outlined"
                    startIcon={<AddIcon />}
                    sx={{ mb: 2, mt: 2 }}
                >
                    Malzeme / Hizmet Ekle
                </Button>

                {kullanilanParcalar.length === 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Henüz malzeme veya hizmet eklenmedi. Eklemek için yukarıdaki butona tıklayın.
                    </Alert>
                )}

                {/* Ödeme ve Notlar */}
                <Divider sx={{ my: 4 }}><Chip label="Ödeme ve Notlar" sx={{ fontWeight: 500 }} /></Divider>

                <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label="Genel Notlar"
                            multiline
                            rows={4}
                            fullWidth
                            value={servisNotlari}
                            onChange={e => setServisNotlari(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Stack spacing={2}>
                            <TextField
                                label="Toplam Tutar (₺)"
                                value={toplamTutar.toFixed(2)}
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                            <TextField
                                label="Ödenen Tutar (₺)"
                                type="number"
                                value={odenenTutar}
                                onChange={e => setOdenenTutar(Number(e.target.value))}
                            />
                            <TextField
                                label="Kalan Tutar (₺)"
                                value={(toplamTutar - odenenTutar).toFixed(2)}
                                fullWidth
                                InputProps={{ readOnly: true }}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Ödeme Tipi</InputLabel>
                                <Select value={odemeTipi} label="Ödeme Tipi" onChange={e => setOdemeTipi(e.target.value)}>
                                    <MenuItem value="Nakit">Nakit</MenuItem>
                                    <MenuItem value="Kredi Kartı">Kredi Kartı</MenuItem>
                                    <MenuItem value="Havale/EFT">Havale/EFT</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                    </Grid>
                </Grid>

                {/* Submit Button */}
                <Box sx={{ mt: 5, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={handleSubmit}
                        disabled={isSaving}
                        sx={{ px: 5, py: 1.5, fontWeight: 'bold' }}
                    >
                        {isSaving ? (
                            <>
                                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                Kaydediliyor...
                            </>
                        ) : (
                            isEditMode ? 'Değişiklikleri Kaydet' : 'Servis Kaydını Oluştur'
                        )}
                    </Button>
                </Box>
            </GlassCard>

            {/* Customer Add Modal */}
            <CustomerFormModal
                isOpen={isCustomerModalOpen}
                onClose={() => setIsCustomerModalOpen(false)}
                onAddCustomer={async (data, device) => {
                    // Mükerrer cihaz denetimi (normalize edilmiş) — müşteri oluşturulmadan önce.
                    if (device) {
                        const duplicate = await findDuplicateDeviceBySerial(device.seriNumarasi);
                        if (duplicate) {
                            alert(duplicateDeviceMessage(duplicate, device.seriNumarasi));
                            return;
                        }
                    }

                    const customerRef = await addDoc(collection(db, 'customers'), {
                        ...data,
                        createdAt: Timestamp.now()
                    });

                    if (device) {
                        await addDoc(collection(db, 'devices'), {
                            ...device,
                            musteriId: customerRef.id,
                            musteriAdSoyad: data.adSoyad,
                            createdAt: Timestamp.now()
                        });
                    }

                    setSelectedCustomerId(customerRef.id);
                }}
            />

            {/* Material Selector Modal */}
            <MaterialServiceSelectorModal
                isOpen={isMaterialModalOpen}
                onClose={() => setIsMaterialModalOpen(false)}
                onSelect={handleMaterialSelect}
            />
        </Box>
    );
};

export default ServisKaydiOlustur;